import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '@/utils/axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  Calendar, 
  Plus, 
  Trash2, 
  Search,
  User,
  FileText,
  ChevronDown,
  ChevronUp,
  Package
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import DateInput from '@/components/DateInput';

export default function DeliveryDates() {
  const navigate = useNavigate();
  const [patientsData, setPatientsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedPatients, setExpandedPatients] = useState({});
  const [newDates, setNewDates] = useState({});
  const [recipientNames, setRecipientNames] = useState({});

  useEffect(() => {
    fetchDeliveryDates();
  }, []);

  const fetchDeliveryDates = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/delivery-dates');
      setPatientsData(response.data);
    } catch (error) {
      console.error('Erro ao buscar datas de entrega:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const togglePatientExpanded = (patientId) => {
    setExpandedPatients(prev => ({
      ...prev,
      [patientId]: !prev[patientId]
    }));
  };

  const handleDateChange = (prescriptionId, value) => {
    setNewDates(prev => ({
      ...prev,
      [prescriptionId]: value
    }));
  };

  const handleRecipientNameChange = (prescriptionId, value) => {
    setRecipientNames(prev => ({
      ...prev,
      [prescriptionId]: value
    }));
  };

  const handleAddDeliveryDate = async (prescriptionId) => {
    const date = newDates[prescriptionId];
    const recipientName = recipientNames[prescriptionId];
    
    if (!date) {
      toast.error('Selecione uma data');
      return;
    }
    
    if (!recipientName || !recipientName.trim()) {
      toast.error('Informe o nome da pessoa que buscou a receita');
      return;
    }

    try {
      await axiosInstance.post('/delivery-dates', {
        prescription_id: prescriptionId,
        date: date,
        recipient_name: recipientName.trim()
      });
      toast.success('Data de entrega registrada com sucesso!');
      setNewDates(prev => ({ ...prev, [prescriptionId]: '' }));
      setRecipientNames(prev => ({ ...prev, [prescriptionId]: '' }));
      fetchDeliveryDates();
    } catch (error) {
      console.error('Erro ao adicionar data:', error);
      toast.error(error.response?.data?.detail || 'Erro ao registrar data de entrega');
    }
  };

  const handleDeleteDeliveryDate = async (deliveryId) => {
    try {
      await axiosInstance.delete(`/delivery-dates/${deliveryId}`);
      toast.success('Registro de entrega removido');
      fetchDeliveryDates();
    } catch (error) {
      console.error('Erro ao deletar:', error);
      toast.error('Erro ao remover registro');
    }
  };

  // Função para normalizar texto (remover acentos)
  const normalizeText = (text) => {
    if (!text) return '';
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  };

  const filteredPatients = patientsData.filter(item => {
    const normalizedName = normalizeText(item.patient.nome);
    const normalizedSearch = normalizeText(searchTerm);
    return normalizedName.includes(normalizedSearch);
  });

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Data de Entrega das Receitas</h1>
              <p className="text-muted-foreground mt-1">
                Registre as datas de entrega dos medicamentos aos pacientes
              </p>
            </div>
          </div>
          <Badge variant="secondary" className="text-lg px-4 py-2">
            <Package className="w-5 h-5 mr-2" />
            {filteredPatients.length} paciente(s)
          </Badge>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Buscar paciente por nome..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 h-12 rounded-full bg-white border-gray-200"
            />
          </div>
        </div>

        {/* Patients List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Carregando...</p>
          </div>
        ) : filteredPatients.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border">
            <User className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-xl font-medium text-muted-foreground">
              {searchTerm ? 'Nenhum paciente encontrado' : 'Nenhum paciente com receitas cadastradas'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredPatients.map((item) => (
              <Card key={item.patient.id} className="overflow-hidden">
                <Collapsible
                  open={expandedPatients[item.patient.id]}
                  onOpenChange={() => togglePatientExpanded(item.patient.id)}
                >
                  <CollapsibleTrigger asChild>
                    <div className="p-4 cursor-pointer hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center text-white font-bold text-lg">
                            {item.patient.nome.charAt(0)}
                          </div>
                          <div>
                            <h3 className="font-bold text-lg">{item.patient.nome}</h3>
                            <p className="text-sm text-muted-foreground">
                              {item.prescriptions.length} receita(s) cadastrada(s)
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge variant="outline">
                            {item.prescriptions.reduce((acc, p) => acc + p.delivery_dates.length, 0)} entrega(s)
                          </Badge>
                          {expandedPatients[item.patient.id] ? (
                            <ChevronUp className="w-5 h-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <CardContent className="pt-0 border-t">
                      <div className="space-y-4 mt-4">
                        {item.prescriptions.map((prescData, idx) => (
                          <div 
                            key={prescData.prescription.id} 
                            className="bg-gray-50 rounded-xl p-4 border border-gray-200"
                          >
                            {/* Prescription Header */}
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center gap-3">
                                <FileText className="w-5 h-5 text-primary" />
                                <div>
                                  <span className="font-semibold">
                                    {prescData.prescription.name || `Receita ${idx + 1}`}
                                  </span>
                                  <Badge variant="secondary" className="ml-2 text-xs">
                                    {prescData.prescription.uso}
                                  </Badge>
                                </div>
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Validade: {formatDate(prescData.prescription.data_validade)}
                              </div>
                            </div>

                            {/* Add New Delivery Date */}
                            <div className="mb-4 p-3 bg-white rounded-lg border">
                              <div className="flex flex-col gap-3">
                                <div className="flex items-center gap-3">
                                  <User className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                                  <Input
                                    type="text"
                                    placeholder="Nome de quem buscou a receita *"
                                    value={recipientNames[prescData.prescription.id] || ''}
                                    onChange={(e) => handleRecipientNameChange(prescData.prescription.id, e.target.value)}
                                    className="flex-1 h-10"
                                  />
                                </div>
                                <div className="flex items-center gap-3">
                                  <Calendar className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                                  <DateInput
                                    value={newDates[prescData.prescription.id] || ''}
                                    onChange={(value) => handleDateChange(prescData.prescription.id, value)}
                                    className="flex-1 h-10"
                                  />
                                  <Button
                                    onClick={() => handleAddDeliveryDate(prescData.prescription.id)}
                                    className="bg-green-600 hover:bg-green-700 text-white"
                                    size="sm"
                                  >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Adicionar Entrega
                                  </Button>
                                </div>
                              </div>
                            </div>

                            {/* Delivery History */}
                            {prescData.delivery_dates.length > 0 ? (
                              <div className="space-y-2">
                                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                                  Histórico de Entregas:
                                </h4>
                                {prescData.delivery_dates.map((delivery, dIdx) => (
                                  <div 
                                    key={delivery.id}
                                    className="flex items-center justify-between p-3 bg-white rounded-lg border"
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                                        dIdx === 0 ? 'bg-green-500' : 'bg-gray-400'
                                      }`}>
                                        {dIdx + 1}
                                      </div>
                                      <div>
                                        <p className="font-medium">
                                          <span className="text-primary">{delivery.recipient_name || 'N/A'}</span>
                                          <span className="text-muted-foreground mx-2">—</span>
                                          {formatDate(delivery.date)}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                          Registrado por: {delivery.delivered_by}
                                        </p>
                                      </div>
                                    </div>
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
                                          <AlertDialogDescription>
                                            Tem certeza que deseja remover este registro de entrega?
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                          <AlertDialogAction
                                            onClick={() => handleDeleteDeliveryDate(delivery.id)}
                                            className="bg-red-600 hover:bg-red-700"
                                          >
                                            Sim, Remover
                                          </AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground text-center py-4">
                                Nenhuma entrega registrada para esta receita
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
